import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../components/ui/Table";
import Loader from "../../components/ui/Loader";
import EmptyState from "../../components/ui/EmptyState";
import { useApi } from "../../services/useApi";
import { useAuth } from "../auth/useAuth";
import { getTeams } from "./teamService";
import TeamEditPage from "./TeamEditPage";

const TeamListPage = () => {
  const api = useApi();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const data = await getTeams(api);
        if (!mounted) return;
        setRows(data || []);
      } catch (err) {
        if (!mounted) return;
        setRows([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [api, token]);

  useEffect(() => {
    const onDocClick = () => setOpenMenuId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleMenuToggle = (e, id) => {
    e.stopPropagation();
    setOpenMenuId((current) => (current === id ? null : id));
  };

  const handleEditTeam = (e, row) => {
    e.stopPropagation();
    setEditingTeam(row);
  };

  const handleTeamSaved = (updatedTeam) => {
    if (!updatedTeam?.publicId) return;
    setRows((current) =>
      (current || []).map((row) =>
        row.publicId === updatedTeam.publicId ? { ...row, ...updatedTeam } : row,
      ),
    );
    setEditingTeam((current) =>
      current?.publicId === updatedTeam.publicId
        ? { ...current, ...updatedTeam }
        : current,
    );
  };

  const handleCopyTeamId = async (e, row) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(row.inviteCode || "");
    } catch {
      // ignore copy errors
    }
  };

  const handleRemoveTeam = (e, row) => {
    e.stopPropagation();
    console.log("Remove team", row);
  };

  const columns = [
    {
      key: "picture",
      label: "Team",
      render: (row) => (
        <div className="cell-team">
          <img
            className="cell-team-photo"
            src={row.pictureUrl || "/icons/Logo_PNG.png"}
            alt={row.name || "Team"}
          />
            <div className="cell-team-meta">
              <div className="cell-team-name">{row.name}</div>
              <div className="team-invite">
                <span className="team-invite-code">{row.inviteCode || "-"}</span>
                <button
                  type="button"
                  className="invite-copy"
                  aria-label="Copy invitation code"
                  onClick={(e) => handleCopyTeamId(e, row)}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path
                      d="M9 4h10v14H9z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M5 8h10v14H5z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      opacity="0.5"
                    />
                    <path
                      d="M7 10h6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ),
      },
    {
      key: "admins",
      label: "Admins",
      render: (row) => {
        const admins = row.members?.filter((m) => m.isAdmin) || [];
        return (
          <div className="cell-avatars">
            {admins.length === 0 && <span>-</span>}
            {admins.map((m) => (
              <div
                key={m.userPublicId || m.publicId || m.email}
                className="cell-member"
                title={m.name || m.username || "Admin"}
              >
                <img
                  src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                  alt={m.name || m.username || "Admin"}
                />
                <span>{m.name || m.username || "Admin"}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "sportsmen",
      label: "Anglers",
      render: (row) => {
        const sportsmen =
          row.members?.filter((m) => m.memberType === "SPORTSMAN" && !m.isAdmin) ||
          [];
        return (
          <div className="cell-avatars">
            {sportsmen.length === 0 && <span>-</span>}
            {sportsmen.map((m) => (
              <div
                key={m.userPublicId || m.publicId || m.email}
                className="cell-member"
                title={m.name || m.username || "Angler"}
              >
                <img
                  src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                  alt={m.name || m.username || "Angler"}
                />
                <span>{m.name || m.username || "Angler"}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "viewers",
      label: "Viewers",
      render: (row) => {
        const viewers =
          row.members?.filter((m) => m.memberType === "VIEWER") || [];
        return (
          <div className="cell-avatars">
            {viewers.length === 0 && <span>-</span>}
            {viewers.map((m) => (
              <div
                key={m.userPublicId || m.publicId || m.email}
                className="cell-member"
                title={m.name || m.username || "Viewer"}
              >
                <img
                  src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                  alt={m.name || m.username || "Viewer"}
                />
                <span>{m.name || m.username || "Viewer"}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "menu",
      label: "",
      render: (row) => (
        <div className="team-menu" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="team-more"
            aria-label="Edit team"
            onClick={(e) => handleMenuToggle(e, row.publicId)}
          >
            ...
          </button>
          {openMenuId === row.publicId && (
            <div className="team-menu-popover">
              <button type="button" onClick={(e) => handleEditTeam(e, row)}>
                <span className="team-menu-icon" aria-hidden="true">
                  ✎
                </span>
                Edit Team
              </button>
              <button type="button" onClick={(e) => handleCopyTeamId(e, row)}>
                <span className="team-menu-icon" aria-hidden="true">
                  ⧉
                </span>
                Copy TeamID
              </button>
              <button type="button" onClick={(e) => handleRemoveTeam(e, row)}>
                <span className="team-menu-icon" aria-hidden="true">
                  🗑
                </span>
                Remove Team
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <h2>Teams</h2>
      {loading && <Loader />}
      {!loading && rows.length === 0 && (
        <EmptyState title="No teams" message="You are not in any team." />
      )}
      {!loading && rows.length > 0 && (
        <Table
          columns={columns}
          rows={rows}
          onRowClick={(row) => navigate(`/teams/${row.publicId}/sessions`)}
        />
      )}
      {editingTeam && (
        <div
          className="title-modal-backdrop"
        >
          <div
            className="title-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <TeamEditPage
              teamIdOverride={editingTeam.publicId}
              onClose={() => setEditingTeam(null)}
              onSaved={handleTeamSaved}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamListPage;

